import { ApiResponse, err, getAxios, postAxios, processError, res } from "./axios";

// Names on the end point are obscured
export const getAllUsers = async ():Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/app/gau`, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error retrieving users.")
    return err(message, status)
  }
}

export const getAllOrganisations = async ():Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/app/gao`, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error retrieving organisations.")
    return err(message, status)
  }
}

export const getAllValidations = async (isPrivateValidation = false):Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/app/gav/${isPrivateValidation}`, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error retrieving validations.")
    return err(message, status)
  }
}

export const getProblematicOrders = async ({ verify = false }: { verify?: boolean } = {}) => {
  try {
    const query = verify ? '?verify=true' : ''
    const result = await getAxios(`/api/v1/app/problematic${query}`, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error retrieving problematic orders.")
    return err(message, status)
  }
}

export const regenerateOrder = async (
  { order_id, order_item_id, transaction_id, uid }:
  { order_id: number; order_item_id: number; transaction_id: string; uid: number }
) => {

  try {
    const result = await postAxios(`/api/v1/app/regenerate-order`, { 
      order_id, 
      order_item_id, 
      transaction_id, uid 
    }, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error regenerating problematic orders.")
    return err(message, status)
  }
}

// --- data_sources ----------------------------------------------------------

export const getAllDataSources = async (
  { include_deleted = false }: { include_deleted?: boolean } = {}
): Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/app/gads`, false, { include_deleted })
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error retrieving data sources.")
    return err(message, status)
  }
}

export const createDataSource = async (payload: Record<string, any>): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/cds`, { ...payload }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error creating data source.")
    return err(message, status)
  }
}

export const updateDataSource = async (
  id: number,
  payload: Record<string, any>
): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/uds`, { id, ...payload }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error updating data source.")
    return err(message, status)
  }
}

/** Soft delete — flips data_sources.deleted rather than removing the row. */
export const setDataSourceDeleted = async (
  id: number,
  deleted: boolean
): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/dds`, { id, deleted }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, `Error ${deleted ? 'deleting' : 'restoring'} data source.`)
    return err(message, status)
  }
}

// --- data_sources_layers ---------------------------------------------------

export const getDataSourceLayers = async (dataSourceId: number): Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/app/gadsl/${dataSourceId}`, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error retrieving layers.")
    return err(message, status)
  }
}

export const createDataSourceLayer = async (
  dataSourceId: number,
  payload: Record<string, any>
): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/cdsl`, { data_source_id: dataSourceId, ...payload }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error creating layer.")
    return err(message, status)
  }
}

export const updateDataSourceLayer = async (
  id: number,
  payload: Record<string, any>
): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/udsl`, { id, ...payload }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error updating layer.")
    return err(message, status)
  }
}

export const deleteDataSourceLayer = async (id: number): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/ddsl`, { id }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error deleting layer.")
    return err(message, status)
  }
}

// --- data_source_vectors ---------------------------------------------------

export const getDataSourceVectors = async (dataSourceId: number): Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/app/gadsv/${dataSourceId}`, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error retrieving vectors.")
    return err(message, status)
  }
}

export const createDataSourceVector = async (
  dataSourceId: number,
  payload: Record<string, any>
): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/cdsv`, { data_source_id: dataSourceId, ...payload }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error creating vector.")
    return err(message, status)
  }
}

export const updateDataSourceVector = async (
  id: number,
  payload: Record<string, any>
): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/udsv`, { id, ...payload }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error updating vector.")
    return err(message, status)
  }
}

export const deleteDataSourceVector = async (id: number): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/ddsv`, { id }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error deleting vector.")
    return err(message, status)
  }
}

// --- data_source_types (global lookup) -------------------------------------

export const getDataSourceTypes = async (): Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/app/gadst`, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error retrieving vector types.")
    return err(message, status)
  }
}

export const createDataSourceType = async (payload: Record<string, any>): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/cdst`, { ...payload }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error creating vector type.")
    return err(message, status)
  }
}

export const updateDataSourceType = async (
  id: number,
  payload: Record<string, any>
): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/udst`, { id, ...payload }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error updating vector type.")
    return err(message, status)
  }
}

export const deleteDataSourceType = async (id: number): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/ddst`, { id }, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error deleting vector type.")
    return err(message, status)
  }
}

export const reloadProxyModels = async (): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/app/reload-proxy`, {}, false);
    return res(result?.data || null);
  } catch (e) {
    const { status, message } = processError(e, "Failed to refresh proxy.");
    return err(message, status);
  }
};

// ---------------------------------------------------------------------------
// Cache note — applies to the existing endpoints too, not just these.
//
// axios-cache-interceptor is set up with methods: ['get', 'post'], so it caches
// POST responses as well as GETs. The `useCache` argument only bypasses the
// hand-rolled sessionStorage layer inside getAxios/postAxios; the interceptor
// sits below that and still serves from its own store.
//
// Two consequences:
//   - a repeated identical mutation (deleting layer 5 twice, saving the same
//     edit again, pressing Regenerate on the same order) can return a cached
//     200 without ever reaching the server
//   - a list GET can return the pre-edit rows straight after a write
//
// The fix is one line in each helper in api/axios.ts — let useCache = false mean
// "don't cache anywhere" rather than "skip one of the two caches":
//
//   // getAxios
//   result = await endpoint.get(reqUrl, {
//     responseType: 'json',
//     params: { ...params },
//     ...(useCache ? {} : { cache: false }),
//   })
//
//   // postAxios
//   result = await (isBlob ? endpointBlob : endpoint)
//     .post(reqUrl, postData, useCache ? {} : { cache: false })
//
// Existing calls already pass false where they mean it (getProblematicOrders,
// regenerateOrder, getAllUsers), so this makes them behave the way they read.
// ---------------------------------------------------------------------------